import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../supabase';
import type { ExternalConnection } from '../external-agents/types';

/**
 * Settings of the workspace's endpoint agents, by agent ID. Reloads when
 * `agentsVersion` changes, e.g. after a save, or on `reload()`.
 */
export function useExternalConnections(
  workspaceId: string | undefined,
  agentsVersion: unknown,
) {
  const [connections, setConnections] = useState<
    Record<string, ExternalConnection>
  >({});
  const [reloads, setReloads] = useState(0);
  const reload = useCallback(() => setReloads((count) => count + 1), []);

  useEffect(() => {
    if (!workspaceId) return;
    let cancelled = false;
    supabase
      .from('external_agent_connections')
      .select(
        'agent_id, format, endpoint_url, auth_type, auth_header_name, model, conversation_mode, session_id_source, session_response_path, request_template, reply_path, last_test_passed_at, created_at',
      )
      .eq('workspace_id', workspaceId)
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          console.error('Loading endpoint agents failed:', error);
          return;
        }
        setConnections(
          Object.fromEntries((data ?? []).map((row) => [row.agent_id, row])),
        );
      });
    return () => {
      cancelled = true;
    };
  }, [workspaceId, agentsVersion, reloads]);

  return { connections, reload };
}
